import { Component, OnInit, Renderer2, inject, signal} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TagGenre } from '../models/models';

import { DecimalPipe } from '@angular/common';
import { ColorUtilityService } from '../services/color-utility/color-utility.service';
import { TechService } from '../services/tech.service';
import { TagGenreDeleteButtonComponent } from './tag-genre-delete-button/tag-genre-delete-button.component';
import { TechsStore } from '../core/stores/techs.store';
import { FormErrorComponent } from '../form-error/form-error.component';
import { ValidationService, ServerErrors } from '../services/validation.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-tag-overview',
  templateUrl: './tag-genre-edit.component.html',
  styleUrls: ['./tag-genre-edit.component.css'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DecimalPipe,
    TagGenreDeleteButtonComponent,
    FormErrorComponent,
    MatProgressSpinnerModule
  ],
})
export class TagGenreEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private techService = inject(TechService);
  private formBuilder = inject(FormBuilder);
  private renderer = inject(Renderer2);
  private techsStore = inject(TechsStore)
  private colorUtilityService = inject(ColorUtilityService);
  private validationService = inject(ValidationService)

  id: string = '';
  tagGenre?: TagGenre;

  genreForm: FormGroup;

  backgroundColor: string = '#6C757D'; // default background color gray
  textColor: string = '#FFFFFF'; // default text color white
  contrastRatio: number = 0;
  contrastRatioThreshhold: number = 4.5;
  addOrEdit: string = '';
  serverErrors: ServerErrors  = {};
  isSubmitting=signal<boolean>(false);

  constructor() {
    this.genreForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(4)]],
      background_color: '',
      text_color: '',
      show_on_cv: [false],
    });
  }

  updateBackgroundColor(color: string) {
    this.backgroundColor = color;

    const newTextColor = this.colorUtilityService.chooseBestContrastColor(this.backgroundColor);
    this.textColor = newTextColor;
    this.updatePlaceholderColor(newTextColor);

    this.calculateContrastRatio();
  }

  updateTextColor(color: string) {
    this.textColor = color;
    this.calculateContrastRatio();

  }

  updatePlaceholderColor(color: string) {
    this.renderer.setStyle(document.documentElement, '--placeholder-color', color);
  }

  calculateContrastRatio() {
    const contrastRatio = this.colorUtilityService.calculateContrastRatio(this.backgroundColor, this.textColor);
    this.contrastRatio = contrastRatio;
  }

  ngOnInit(): void {
    this.genreForm.setValue({
      name: '',
      background_color: this.backgroundColor,
      text_color: this.textColor,
      show_on_cv: false,
    });

    this.calculateContrastRatio();
    this.route.params.subscribe((params) => {
      this.id = params['id'];
      if (this.id) {
        this.techService.getTagGenreById(this.id).subscribe({
          next: (genre) => {
            if (genre.data) {
              this.tagGenre = genre.data;
              this.genreForm.setValue({
                name: genre.data.name,
                background_color: genre.data.backgroundColor,
                text_color: genre.data.textColor,
                show_on_cv: genre.data.allowOnCv ?? false,
              });
              this.backgroundColor = genre.data.backgroundColor;
              this.textColor = genre.data.textColor;
            }
            if (genre.error) {
              return;
            }
          },
          error: (e) => { },
        });
      }
    });

    if (this.id) {
      this.addOrEdit = "Edit Tag Genre";
    } else {
      this.addOrEdit = "Add Tag Genre";
    }
  }

  submit(): void {
    if (this.genreForm.invalid) {
      return;
    }

    const updatedTagGenre: TagGenre = {
      ...(this.tagGenre ?? {}),
      name: this.genreForm.get('name')?.value,
      backgroundColor: this.backgroundColor,
      textColor: this.textColor,
      allowOnCv: this.genreForm.get('show_on_cv')?.value ?? false,
    };
    this.isSubmitting.set(true);
    if (this.id) {
      this.techService.putTagGenre(this.id, updatedTagGenre).subscribe({
        next: async (_) => {
          this.isSubmitting.set(false);
          this.validationService.showSnackBar('Tag genre was updated successfully!', 'success-snackbar');
          await this.router.navigate(['/tag']);
        },
        error: (err) => {
          this.isSubmitting.set(false);
          if (err.status === 400 && err.error) {
            if (err.error.errors) {
              this.serverErrors = err.error.errors
            }
          }
          console.error(err);
        }
      });
    } else {
      this.techService.postTagGenre(updatedTagGenre).subscribe({
        next: async (_) => {
          this.techsStore.getTags()
          this.isSubmitting.set(false);
          this.validationService.showSnackBar('Tag genre was created successfully!', 'success-snackbar');
          await this.router.navigate(['/tag']);
        },
        error: (err) => {
          this.isSubmitting.set(false);
          if (err.status === 400 && err.error) {
            if (err.error.errors) {
              this.serverErrors = err.error.errors
            }
          }
          console.error(err);
        }
      });
    }
  }

  async back(): Promise<void> {
    await this.router.navigate(['/tag']);
  }
}
